import React, { createContext, useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import NETWORK from '../config/network';
import CONTRACT_ADDRESSES from '../contracts/addresses';
import { USDT_ABI, UVW_CORE_ABI, UVW_MATRIX_ABI, UVW_POOLS_ABI, UVW_VIEWS_ABI } from '../contracts/abis';

export const Web3Context = createContext();

export function Web3Provider({ children }) {
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [contracts, setContracts] = useState({});
  const [readContracts, setReadContracts] = useState({});
  const [isConnecting, setIsConnecting] = useState(false);
  const [isCorrectNetwork, setIsCorrectNetwork] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const switchingRef = useRef(false);

  function getEthereum() {
    if (typeof window === 'undefined') return null;
    return window.ethereum || null;
  }

  async function switchNetwork() {
    const ethereum = getEthereum();
    if (!ethereum || switchingRef.current) return false;
    switchingRef.current = true;
    try {
      await ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: NETWORK.chainId }]
      });
      switchingRef.current = false;
      return true;
    } catch (switchError) {
      if (switchError.code === 4902) {
        try {
          await ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: NETWORK.chainId,
              chainName: NETWORK.chainName,
              rpcUrls: NETWORK.rpcUrls,
              blockExplorerUrls: NETWORK.blockExplorerUrls,
              nativeCurrency: NETWORK.nativeCurrency
            }]
          });
          switchingRef.current = false;
          return true;
        } catch (addError) {
          console.error('Failed to add network:', addError);
        }
      } else {
        console.error('Failed to switch network:', switchError);
      }
    }
    switchingRef.current = false;
    return false;
  }

  async function checkNetwork(web3Provider) {
    try {
      const network = await web3Provider.getNetwork();
      return network.chainId === NETWORK.chainIdDecimal;
    } catch {
      return false;
    }
  }

  function initContracts(web3Signer) {
    const readProvider = new ethers.providers.JsonRpcProvider(NETWORK.rpcUrls[0]);

    const writeContracts = {
      usdt: new ethers.Contract(CONTRACT_ADDRESSES.USDT, USDT_ABI, web3Signer),
      core: new ethers.Contract(CONTRACT_ADDRESSES.UVW_Core, UVW_CORE_ABI, web3Signer),
      matrix: new ethers.Contract(CONTRACT_ADDRESSES.UVW_Matrix, UVW_MATRIX_ABI, web3Signer),
      pools: new ethers.Contract(CONTRACT_ADDRESSES.UVW_Pools, UVW_POOLS_ABI, web3Signer),
      views: new ethers.Contract(CONTRACT_ADDRESSES.UVW_Views, UVW_VIEWS_ABI, web3Signer)
    };

    const readOnly = {
      usdt: new ethers.Contract(CONTRACT_ADDRESSES.USDT, USDT_ABI, readProvider),
      core: new ethers.Contract(CONTRACT_ADDRESSES.UVW_Core, UVW_CORE_ABI, readProvider),
      matrix: new ethers.Contract(CONTRACT_ADDRESSES.UVW_Matrix, UVW_MATRIX_ABI, readProvider),
      pools: new ethers.Contract(CONTRACT_ADDRESSES.UVW_Pools, UVW_POOLS_ABI, readProvider),
      views: new ethers.Contract(CONTRACT_ADDRESSES.UVW_Views, UVW_VIEWS_ABI, readProvider)
    };

    setContracts(writeContracts);
    setReadContracts(readOnly);
    return { writeContracts, readOnly };
  }

  async function fetchUserId(coreContract, walletAddress) {
    try {
      const id = await coreContract.walletToUserId(walletAddress);
      const idNum = id.toNumber();
      setUserId(idNum > 0 ? idNum : null);
      return idNum;
    } catch {
      setUserId(null);
      return 0;
    }
  }

  async function checkAdminStatus(readOnly, address) {
    try {
      const [coreOwner, coreAdmin] = await Promise.all([
        readOnly.core.owner(),
        readOnly.core.admin()
      ]);
      const addr = address.toLowerCase();
      const ownerMatch = addr === coreOwner.toLowerCase();
      const adminMatch = addr === coreAdmin.toLowerCase();
      setIsOwner(ownerMatch);
      setIsAdmin(ownerMatch || adminMatch);
    } catch {
      setIsAdmin(false);
      setIsOwner(false);
    }
  }

  async function connectWallet() {
    const ethereum = getEthereum();
    if (!ethereum) {
      setConnectionError('No wallet detected. Please install Trust Wallet or MetaMask.');
      return;
    }
    setIsConnecting(true);
    setConnectionError(null);
    try {
      const accounts = await ethereum.request({ method: 'eth_requestAccounts' });
      if (!accounts || accounts.length === 0) {
        setConnectionError('No accounts found. Please unlock your wallet.');
        setIsConnecting(false);
        return;
      }
      const web3Provider = new ethers.providers.Web3Provider(ethereum, 'any');
      const correct = await checkNetwork(web3Provider);
      if (!correct) {
        const switched = await switchNetwork();
        if (!switched) {
          setConnectionError('Please switch to the correct network in your wallet.');
          setIsConnecting(false);
          return;
        }
        await new Promise(r => setTimeout(r, 1000));
      }
      const refreshedProvider = new ethers.providers.Web3Provider(ethereum, 'any');
      const web3Signer = refreshedProvider.getSigner();
      const address = await web3Signer.getAddress();
      setProvider(refreshedProvider);
      setSigner(web3Signer);
      setAccount(address);
      setIsCorrectNetwork(true);
      const { readOnly } = initContracts(web3Signer);
      await Promise.all([
        fetchUserId(readOnly.core, address),
        checkAdminStatus(readOnly, address)
      ]);
    } catch (err) {
      console.error('Connection failed:', err);
      setConnectionError(err.message || 'Failed to connect wallet');
    }
    setIsConnecting(false);
  }

  function disconnectWallet() {
    setAccount(null);
    setProvider(null);
    setSigner(null);
    setUserId(null);
    setIsAdmin(false);
    setIsOwner(false);
    setContracts({});
    setReadContracts({});
    setIsCorrectNetwork(false);
    setConnectionError(null);
  }

  useEffect(() => {
    const ethereum = getEthereum();
    if (!ethereum) return;

    function handleAccountsChanged(accounts) {
      if (!accounts || accounts.length === 0) {
        disconnectWallet();
      } else if (accounts[0] !== account) {
        connectWallet();
      }
    }

    function handleChainChanged() {
      if (switchingRef.current) return;
      connectWallet();
    }

    ethereum.on('accountsChanged', handleAccountsChanged);
    ethereum.on('chainChanged', handleChainChanged);

    return () => {
      ethereum.removeListener('accountsChanged', handleAccountsChanged);
      ethereum.removeListener('chainChanged', handleChainChanged);
    };
  }, [account]);

  async function refreshUserId() {
    if (readContracts.core && account) {
      return await fetchUserId(readContracts.core, account);
    }
    return 0;
  }

  return (
    <Web3Context.Provider value={{
      account, provider, signer, userId, isAdmin, isOwner, contracts, readContracts,
      isConnecting, isCorrectNetwork, connectionError,
      connectWallet, disconnectWallet, refreshUserId, switchNetwork
    }}>
      {children}
    </Web3Context.Provider>
  );
}
